import type { QueryClient, QueryKey } from '@tanstack/react-query';
import type { AssignedProduct, RosterEntryCreators } from '@discovr/types';
import { campaignKeys } from './queryKeys';
import { computeRosterEstimates } from './rosterEstimates';

/** Snapshot of every cached Creators page, for optimistic rollback. */
export type CreatorsCacheSnapshot = [QueryKey, unknown][];

/**
 * Optimistically apply a deliverable selection to every cached Creators page.
 *
 * Uses setQueriesData (prefix/partial match on ['roster', id, 'creators', *]) —
 * NOT setQueryData with the exact base key, which resolves to filters `{}` and
 * misses the live query keyed with {status,page,limit} (a phantom write). The
 * sibling patchCreatorStatus documents the same trap.
 *
 * Recomputes reach only. Money (`money`, estimated_budget, estimated_cpv) is Go's:
 * it is cleared so the row shows "—" until the refetch brings the new total.
 * Returns a snapshot for rollback.
 */
export function applyOptimisticDeliverableSelection(
  qc: QueryClient,
  campaignId: string,
  rosterEntryId: string,
  types: string[],
): CreatorsCacheSnapshot {
  const filter = { queryKey: campaignKeys.rosterTab(campaignId, 'creators') };
  const previous = qc.getQueriesData(filter);

  qc.setQueriesData(filter, (old: unknown) => {
    if (!old || typeof old !== 'object' || !Array.isArray((old as { entries?: unknown }).entries)) {
      return old;
    }
    const paged = old as { entries: RosterEntryCreators[]; total: number };
    return {
      ...paged,
      entries: paged.entries.map((e) => {
        if (e.id !== rosterEntryId) return e;
        const updatedDeliverables = e.deliverables.map((d) => ({
          ...d,
          is_selected: types.includes(d.type),
        }));
        const { reach } = computeRosterEstimates(updatedDeliverables, e.snapshot_metrics);
        // Money is Go's: drop the old totals so the row shows "—" until the refetch lands.
        const { money: _money, estimated_budget: _budget, estimated_cpv: _cpv, ...rest } = e;
        return {
          ...rest,
          deliverables: updatedDeliverables,
          estimated_reach: reach,
        } as RosterEntryCreators;
      }),
    };
  });

  return previous;
}

/** Restore every snapshotted Creators page (used on mutation error). */
export function rollbackCreatorsPages(qc: QueryClient, snapshot: CreatorsCacheSnapshot): void {
  snapshot.forEach(([key, data]) => {
    qc.setQueryData(key, data);
  });
}

/**
 * Optimistically set a creator's assigned products on every cached roster page
 * (Multi-Product Campaigns). Matches the whole roster subtree (`['roster', id]`)
 * so the chips update instantly on whichever tab is showing them.
 *
 * Handles BOTH cached shapes: paged `{ entries, total }` (Creators tab) AND a
 * bare `TRow[]` array (the Confirmed/Finalized + Live pills cache their
 * status-filtered row array directly via useRosterStatusTabs' buildQueryOptions).
 * The bare-array case is why an earlier `{entries}`-only guard silently no-op'd
 * on the Confirmed tab — the chips never updated until a refetch. Any other
 * shape is left as-is. Returns a snapshot for rollback (restore with
 * rollbackRosterPages).
 */
type RosterRow = { id?: string; assigned_products?: AssignedProduct[] };

export function applyOptimisticProductAssignment(
  qc: QueryClient,
  campaignId: string,
  rosterEntryId: string,
  assignedProducts: AssignedProduct[],
): CreatorsCacheSnapshot {
  const filter = { queryKey: campaignKeys.rosterAll(campaignId) };
  const previous = qc.getQueriesData(filter);

  const patchRow = (e: RosterRow): RosterRow =>
    e.id === rosterEntryId ? { ...e, assigned_products: assignedProducts } : e;

  qc.setQueriesData(filter, (old: unknown) => {
    // Bare-array cache (Finalized/Live pills store TRow[] directly).
    if (Array.isArray(old)) {
      return (old as RosterRow[]).map(patchRow);
    }
    // Paged cache ({ entries, total } — Creators tab + detail pages).
    if (old && typeof old === 'object' && Array.isArray((old as { entries?: unknown }).entries)) {
      const paged = old as { entries: RosterRow[] };
      return { ...paged, entries: paged.entries.map(patchRow) };
    }
    return old;
  });

  return previous;
}

/** Restore every snapshotted roster page (used on mutation error). */
export const rollbackRosterPages = rollbackCreatorsPages;

type CreatorsPage = { entries: RosterEntryCreators[]; total: number };

/**
 * Move a creator row across every cached Creators-tab pill (Pending/Approved/
 * All are three SEPARATE server-filtered queries — see queryKeys.rosterTab —
 * not one shared list, so a plain status-field patch only updates the row
 * wherever it already sits; it can never make the row appear in a pill whose
 * cache never had it (e.g. Approved right after a Pending→approve). This
 * upserts/evicts the row from EVERY cached pill based on that pill's own
 * `status` filter (embedded in its query key), so the move is instant instead
 * of waiting on the mutation's background `invalidateQueries` refetch.
 */
export function upsertCreatorAcrossPills(
  qc: QueryClient,
  campaignId: string,
  creatorId: string,
  newStatus: RosterEntryCreators['status'],
): void {
  const filter = { queryKey: campaignKeys.rosterTab(campaignId, 'creators') };
  const pages = qc.getQueriesData<CreatorsPage>(filter);

  // The mutation only knows id + new status, not the row's full shape
  // (name, avatar, deliverables, snapshot metrics, …) — recover it from
  // whichever cached pill still holds it before it's evicted below.
  let sourceEntry: RosterEntryCreators | undefined;
  for (const [, data] of pages) {
    sourceEntry = data?.entries?.find((e) => e.id === creatorId);
    if (sourceEntry) break;
  }
  if (!sourceEntry) return;
  const updatedEntry: RosterEntryCreators = { ...sourceEntry, status: newStatus };

  pages.forEach(([key, data]) => {
    if (!data || !Array.isArray(data.entries)) return;
    const filters = (key[3] ?? {}) as { status?: string };
    // No status filter = the "All" pill, which belongs to every non-terminal
    // status; a status filter is a comma-joined allow-list for that pill.
    const belongs = !filters.status || filters.status.split(',').includes(newStatus);
    const exists = data.entries.some((e) => e.id === creatorId);
    if (belongs === exists) {
      if (exists) {
        qc.setQueryData<CreatorsPage>(key, {
          ...data,
          entries: data.entries.map((e) => (e.id === creatorId ? updatedEntry : e)),
        });
      }
      return;
    }
    const entries = belongs
      ? [updatedEntry, ...data.entries]
      : data.entries.filter((e) => e.id !== creatorId);
    qc.setQueryData<CreatorsPage>(key, { ...data, entries, total: data.total + (belongs ? 1 : -1) });
  });
}

/**
 * Evict a creator row from every cached Creators-tab pill — used for reject/
 * remove, which take the row out of the funnel entirely (no destination pill
 * to move it into, unlike upsertCreatorAcrossPills).
 */
export function removeCreatorFromAllPills(
  qc: QueryClient,
  campaignId: string,
  creatorId: string,
): void {
  const filter = { queryKey: campaignKeys.rosterTab(campaignId, 'creators') };
  qc.setQueriesData<CreatorsPage>(filter, (old) => {
    if (!old || !Array.isArray(old.entries)) return old;
    const entries = old.entries.filter((e) => e.id !== creatorId);
    if (entries.length === old.entries.length) return old;
    return { ...old, entries, total: old.total - 1 };
  });
}
